<template>
    <div
        id="setup-element"
        ref="setupElementContainer"
    >
        <!--Stripe.js injects the Payment Element-->
    </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { graphql } from '~/resources/graphql'
import {
    loadStripe,
    type Stripe,
    type StripeElements,
    type StripeError,
    type StripePaymentElement
} from '@stripe/stripe-js'
import { useRpQuery } from '~/composables/graphql'
import type { TokenPaymentMethod } from '~/components/Checkout/Sections/Payment/payment-method'
import { useCheckoutFormProcessing } from '~/components/Checkout/Forms/use-checkout-form'

const props = defineProps<{
    paymentSourceId: string
    checkoutId?: string
}>()

const emit = defineEmits<{
    (e: 'paymentMethod', payload: TokenPaymentMethod | undefined): void
    (e: 'displayStripeError', payload: StripeError): void
}>()

const paymentSourceData = ref<{
    stripeSetupIntentClientSecret: string
    stripeAccountId: string | null
    stripeReturnUrl: string
    stripePublishKey: string
    stripeCurrency: string
}>()

let stripe: Stripe | null = null
let elements: StripeElements
let paymentElement: StripePaymentElement | undefined
let paymentElementReady = false
let disposed = false
let initializationProcessing = false
const setupElementContainer = ref<HTMLElement>()

const { incrementProcessing, decrementProcessing } = useCheckoutFormProcessing()

function releaseInitializationProcessing(): void {
    if (initializationProcessing) {
        // Stripe can fail and unmount concurrently, so release this form's lock only once.
        initializationProcessing = false
        decrementProcessing()
    }
}

function displayInitializationError(error: unknown): void {
    // Initialization failures occur before Stripe can return a confirmation error.
    emit('displayStripeError', {
        type: 'api_error',
        message: error instanceof Error ? error.message : undefined
    })
}

// Setup forms have no amount, so source and checkout changes are handled by the parent key.
const query = useRpQuery()
async function initializeStripeSetupForm(): Promise<void> {
    try {
        const response = await query(
            graphql(/* GraphQL */ `
                mutation CreateStripeSetupIntent(
                    $input: CreateStripeSetupIntentInput!
                ) {
                    createStripeSetupIntent(input: $input) {
                        stripeSetupIntentClientSecret
                        stripeAccountId
                        stripeReturnUrl
                        stripePublishKey
                        stripeCurrency
                    }
                }
            `),
            {
                input: {
                    paymentSourceId: props.paymentSourceId,
                    checkoutId: props.checkoutId
                }
            }
        )
        if (disposed) {
            return
        }

        const intentData = response.createStripeSetupIntent
        const nextStripe = await loadStripe(intentData.stripePublishKey, {
            stripeAccount: intentData.stripeAccountId ?? undefined
        })
        if (disposed) {
            return
        }

        if (nextStripe === null) {
            throw new Error('Stripe object not initialized')
        }

        const nextElements = nextStripe.elements({
            clientSecret: intentData.stripeSetupIntentClientSecret,
            currency: intentData.stripeCurrency
        })
        const nextPaymentElement = nextElements.create('payment', {})
        if (setupElementContainer.value === undefined) {
            nextPaymentElement.destroy()
            throw new Error('Stripe setup element container not initialized')
        }

        nextPaymentElement.once('ready', () => {
            if (!disposed && paymentElement === nextPaymentElement) {
                paymentElementReady = true
                // Elements is now safe to confirm, so allow the containing form to submit.
                releaseInitializationProcessing()
            }
        })
        nextPaymentElement.once('loaderror', ({ error }) => {
            if (!disposed && paymentElement === nextPaymentElement) {
                paymentElementReady = false
                paymentSourceData.value = undefined
                paymentElement = undefined
                stripe = null
                nextPaymentElement.destroy()
                // Initialization cannot recover without a refresh, so restore the form controls.
                releaseInitializationProcessing()
                emit('displayStripeError', error)
            }
        })

        stripe = nextStripe
        elements = nextElements
        paymentSourceData.value = intentData
        paymentElement = nextPaymentElement
        nextPaymentElement.mount(setupElementContainer.value)
    } catch (error) {
        if (!disposed) {
            paymentElementReady = false
            paymentSourceData.value = undefined
            paymentElement?.destroy()
            paymentElement = undefined
            stripe = null
            releaseInitializationProcessing()
            displayInitializationError(error)
        }
    }
}

onMounted(() => {
    // Hold the shared form lock before the provider request begins.
    initializationProcessing = true
    incrementProcessing()
    void initializeStripeSetupForm()
})

onBeforeUnmount(() => {
    // A replacement source, checkout, or refresh owns subsequent provider work.
    disposed = true
    releaseInitializationProcessing()
    paymentElementReady = false
    paymentSourceData.value = undefined
    paymentElement?.destroy()
    paymentElement = undefined
    stripe = null
})

//--------------------------------------------
// submit stripe form - called from parent
// to get stored payment method token
//--------------------------------------------
async function submitStripeElementsForm(): Promise<boolean> {
    if (
        stripe === null ||
        paymentSourceData.value === undefined ||
        !paymentElementReady
    ) {
        // Callers must not treat an unready Element as a submitted payment method.
        return false
    }

    // Ignore a confirmation result from an Element that has since been replaced.
    const submittedStripe = stripe
    const submittedElements = elements
    const submittedPaymentSourceData = paymentSourceData.value
    const result = await submittedStripe.confirmSetup({
        elements: submittedElements,
        confirmParams: {
            expand: ['payment_method'],
            return_url: submittedPaymentSourceData.stripeReturnUrl
        },
        redirect: 'if_required'
    })

    if (disposed || paymentSourceData.value !== submittedPaymentSourceData) {
        return false
    }

    if (result.setupIntent) {
        if (result.setupIntent.payment_method === null) {
            throw new Error('Payment method null')
        }
        if (typeof result.setupIntent.payment_method === 'string') {
            throw new Error('Payment method is not expanded')
        }

        const paymentMethod: TokenPaymentMethod = {
            type: 'TokenPaymentMethod',
            token: 'STRIPE_PM|' + result.setupIntent.payment_method.id,
            isCard: false,
            paymentTodayAllowed: false,
            brand: result.setupIntent.payment_method.type,
            lastFour: null,
            accountExpirationDate: null
        }

        if (result.setupIntent.payment_method.card !== undefined) {
            paymentMethod.isCard = true
            paymentMethod.brand =
                result.setupIntent.payment_method.card.brand.toLocaleUpperCase()
            paymentMethod.lastFour =
                result.setupIntent.payment_method.card.last4
            paymentMethod.accountExpirationDate =
                result.setupIntent.payment_method.card.exp_year +
                '-' +
                result.setupIntent.payment_method.card.exp_month +
                '-01'
        }

        emit('paymentMethod', paymentMethod)
        return true
    }

    if (result.error) {
        emit('displayStripeError', result.error)
        // Confirmation ran, even though Stripe rejected the entered payment details.
        return true
    }

    // Stripe's result union should always provide either an intent or an error.
    throw new Error('Stripe did not return a setup result')
}

defineExpose({
    submitStripeElementsForm
})
</script>
